import React from "react"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import type {
  TabsRootChangeEventDetails,
  TabsRootOrientation
} from "@base-ui/react/tabs"
import { TabsOrientationContext } from "./tabs-context"
import { TabsList } from "./tabs-list"
import { TabsPanel } from "./tabs-panel"
import { TabsTab } from "./tabs-tab"
import type { StrictOmit } from "../utils"

export type { TabsListProps } from "./tabs-list"
export type { TabsPanelProps } from "./tabs-panel"
export type { TabsTabProps } from "./tabs-tab"
export type {
  TabsRootChangeEventDetails,
  TabsRootOrientation
} from "@base-ui/react/tabs"

// =============================================================================
// Props
// =============================================================================

export type TabsProps = {
  /** A `Tabs.List` and a `Tabs.Panel` for each of its tabs */
  children?: React.ReactNode
  /**
   * Layout classes. `Tabs` doesn't lay out its list and panels, so add the
   * layout here when they're its direct children, such as `flex` for vertical
   * tabs beside their panels.
   */
  className?: string
  /** The value of the selected tab (controlled) */
  value?: TabsPrimitive.Tab.Value
  /** The value of the tab selected at first (uncontrolled) */
  defaultValue?: TabsPrimitive.Tab.Value
  /** Called when the selected tab changes */
  onValueChange?: (
    value: TabsPrimitive.Tab.Value,
    eventDetails: TabsRootChangeEventDetails
  ) => void
  /** Whether the tabs sit in a row above the panels or in a column beside them */
  orientation?: TabsRootOrientation
} & StrictOmit<
  React.ComponentPropsWithoutRef<"div">,
  // Owned by the root
  "children" | "className" | "defaultValue"
>

// =============================================================================
// Component
// =============================================================================

/**
 * Tabs for switching between panels of content on the same page. Renders a
 * `role="tablist"` with arrow-key navigation, and shows the `Tabs.Panel` whose
 * `value` matches the selected `Tabs.Tab`.
 *
 * Compose with a `Tabs.List` of `Tabs.Tab`s, and a `Tabs.Panel` for each tab.
 * The list and panels can sit anywhere inside `Tabs`, such as the list in a
 * page header and the panels in the page body, so `Tabs` adds no layout of its
 * own.
 *
 * For navigation between pages, where each tab has its own URL, use `TabLinks`
 * instead.
 */
function TabsRoot({
  children,
  className,
  value,
  defaultValue,
  onValueChange,
  orientation = "horizontal",
  ...props
}: TabsProps) {
  return (
    <TabsOrientationContext.Provider value={orientation}>
      <TabsPrimitive.Root
        {...props}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        orientation={orientation}
        className={className}
        data-slot="tabs"
      >
        {children}
      </TabsPrimitive.Root>
    </TabsOrientationContext.Provider>
  )
}

TabsRoot.displayName = "Tabs"

// =============================================================================
// Compound component
// =============================================================================

interface TabsComponent {
  (props: TabsProps): React.JSX.Element
  List: typeof TabsList
  Tab: typeof TabsTab
  Panel: typeof TabsPanel
}

export const Tabs: TabsComponent = Object.assign(TabsRoot, {
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel
} as const)
